import React, { useState, useEffect } from "react";
import { ethers } from "ethers";
import {
  VStack,
  Text,
  HStack,
  Image,
  Box,
  IconButton,
  Stat,
  Tooltip,
} from "@chakra-ui/react";
import Vault from "../../contracts/Vault.json";
import PositionRouter from "../../contracts/PositionRouter.json";
import { RepeatIcon } from "@chakra-ui/icons";
import { provider, link } from "../../config";

const LinkLiquidityInfo = () => {
  const [wethLiquidity, setLinkLiquidity] = useState({
    longLiquidityUsd: "",
    shortLiquidityUsd: "",
  });
  const [status, setStatus] = useState("");

  const vaultAddress = Vault.address;
  const positionRouterAddress = PositionRouter.address;

  const fetchLongLiquidityInUsd = async (indexToken) => {
    try {
      const vault = new ethers.Contract(vaultAddress, Vault.abi, provider);
      const positionRouter = new ethers.Contract(
        positionRouterAddress,
        PositionRouter.abi,
        provider
      );

      const maxGlobalLongSizes = await positionRouter.maxGlobalLongSizes(
        indexToken
      );
      const guaranteedUsd = await vault.guaranteedUsd(indexToken);

      const availableUsd = maxGlobalLongSizes.gt(0)
        ? maxGlobalLongSizes.sub(guaranteedUsd)
        : ethers.BigNumber.from(0);

      return availableUsd;
    } catch (err) {
      console.error(`Error fetching long liquidity for LINK:`, err);
      throw err;
    }
  };

  const fetchShortLiquidityInUsd = async (indexToken) => {
    try {
      const vault = new ethers.Contract(vaultAddress, Vault.abi, provider);
      const positionRouter = new ethers.Contract(
        positionRouterAddress,
        PositionRouter.abi,
        provider
      );

      const maxGlobalShortSizes = await positionRouter.maxGlobalShortSizes(
        indexToken
      );
      const globalShortSizes = await vault.globalShortSizes(indexToken);

      const availableUsd = maxGlobalShortSizes.gt(0)
        ? maxGlobalShortSizes.sub(globalShortSizes)
        : ethers.BigNumber.from(0);

      return availableUsd;
    } catch (err) {
      console.error(`Error fetching short liquidity for LINK:`, err);
      throw err;
    }
  };

  const formatUsdValue = (value) => {
    const formattedValue = ethers.utils.formatUnits(value, 30);
    return parseFloat(formattedValue).toLocaleString("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
  };

  const fetchLinkLiquidity = async () => {
    try {
      setStatus("Loading available liquidity...");
      const longLiquidityUsd = await fetchLongLiquidityInUsd(link);
      const shortLiquidityUsd = await fetchShortLiquidityInUsd(link);

      setLinkLiquidity({
        longLiquidityUsd: formatUsdValue(longLiquidityUsd),
        shortLiquidityUsd: formatUsdValue(shortLiquidityUsd),
      });

      setStatus("");
    } catch (err) {
      console.error("Error fetching LINK liquidity data:", err);
      setStatus("Error fetching LINK liquidity data.");
    }
  };

  const reload = () => {
    fetchLinkLiquidity();
  };

  useEffect(() => {
    fetchLinkLiquidity();
  }, []);

  return (
    <Box h="100%" w="100%" minWidth='290px' fontFamily='Sentinel' mb={1}>
      <HStack justify="space-between" mb={1} px={2}>
        <HStack gap="4px">
          <Image boxSize="16px" src="./chainlink.png" alt="LINK" />
          <Text fontSize="13px" as="b">LINK Liquidity</Text>
        </HStack>
        <Tooltip label="Reload Liquidity" aria-label="reload" placement="top" hasArrow>
          <IconButton
            aria-label="Reload Liquidity"
            icon={<RepeatIcon />}
            colorScheme="blue"
            size="xs"
            variant="unstyled"
            onClick={reload}
          />
        </Tooltip>
      </HStack>

      {!status ? (
        <Stat w="100%">
          <HStack bg="gray.50" w="100%" h="auto" px={2} py={0} borderBottom="1px solid silver" justify="space-between">
            <Text fontSize="sm">Long</Text>
            <Text fontSize="sm" fontWeight="bold">{wethLiquidity.longLiquidityUsd}</Text>
          </HStack>
          <HStack bg="gray.50" w="100%" h="auto" px={2} py={0} borderBottom="1px solid silver" justify="space-between">
            <Text fontSize="sm">Short</Text>
            <Text fontSize="sm" fontWeight="bold">{wethLiquidity.shortLiquidityUsd}</Text>
          </HStack>
        </Stat>
      ) : (
        <Text color="red.500" fontSize="xs" mt={2} px={2}>{status}</Text>
      )}
    </Box>
  );
};

export default LinkLiquidityInfo;